// Client side of the confirmed chain: explicit language -> first browser
// language that maps to a published one -> base language. A regional variant
// maps only to its own language (en-GB -> en, fr-CA -> fr); there is no search
// for a "similar" other language. Phase 3 passes the profile language from the
// bootstrap as `explicit`; until then clients pass only the browser list.

export type ResolutionSource = "explicit_profile" | "browser" | "platform_default" | "fallback_unavailable";

export interface LanguageResolution {
  readonly language: string;
  readonly source: ResolutionSource;
}

/** Canonical case of a BCP 47 tag (en-gb -> en-GB, zh-hant-tw -> zh-Hant-TW); null if it is not a usable tag. */
export function canonicalTag(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag.trim().replace(/_/g, "-"))[0] ?? null;
  } catch {
    return null;
  }
}

function match(candidate: string, published: readonly string[]): string | null {
  const tag = canonicalTag(candidate);
  if (!tag) return null;
  if (published.includes(tag)) return tag;
  const primary = tag.split("-")[0];
  return published.includes(primary) ? primary : null;
}

export function resolveLanguage(input: {
  readonly explicit?: string | null;
  readonly browser?: readonly string[];
  readonly published: readonly string[];
  readonly base: string;
}): LanguageResolution {
  const { explicit, browser = [], published, base } = input;
  const explicitTag = explicit ? canonicalTag(explicit) : null;
  if (explicitTag && published.includes(explicitTag)) return { language: explicitTag, source: "explicit_profile" };
  for (const candidate of browser) {
    const language = match(candidate, published);
    if (language) return { language, source: explicit ? "fallback_unavailable" : "browser" };
  }
  return { language: base, source: explicit ? "fallback_unavailable" : "platform_default" };
}
